
import React from 'react';
import { Target, Lightbulb, Heart, Globe, MessageCircle } from 'lucide-react';

const About: React.FC = () => {
  return (
    <div className="min-h-screen relative">
       {/* Background Image with Blur */}
      <div 
        className="fixed inset-0 bg-fixed bg-cover bg-center z-0"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')" }}
      >
        <div className="absolute inset-0 bg-white/40 backdrop-blur-[10px]"></div>
      </div>

      <div className="relative z-10">
        {/* Hero */}
        <section className="bg-navy/90 py-20 text-center text-white backdrop-blur-sm">
            <div className="container mx-auto px-4">
                <h1 className="text-4xl md:text-6xl font-bold mb-6">Le Démarcheur Facilitateur</h1>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto">Votre partenaire de confiance dans l'immobilier béninois.</p>
            </div>
        </section>

        <div className="container mx-auto px-4 py-16 max-w-5xl">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20 items-center">
                <div>
                    <h2 className="text-3xl font-bold text-navy mb-6">Notre Vision</h2>
                    <p className="text-gray-900 font-medium text-lg leading-relaxed mb-6 bg-white/60 p-6 rounded-2xl backdrop-blur-sm">
                        Devenir la référence incontournable de l'immobilier au Bénin en offrant une expérience 
                        <span className="font-bold text-orange"> transparente</span>, 
                        <span className="font-bold text-orange"> fluide</span> et 
                        <span className="font-bold text-orange"> humaine</span>.
                    </p>
                    <div className="h-1 w-20 bg-orange rounded"></div>
                </div>
                <div className="flex items-center justify-center">
                    <img 
                      src="https://res.cloudinary.com/dgsmu4bgt/image/upload/v1764432307/Generated_Image_September_18_2025_-_12_13PM_xl3nsm.jpg" 
                      alt="Vision" 
                      className="rounded-3xl shadow-2xl max-h-80 w-auto object-cover transform rotate-2 hover:rotate-0 transition duration-500"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20 items-center md:flex-row-reverse">
                 <div className="flex items-center justify-center md:order-1">
                    {/* Custom SVG Icon for Mission (Arrow hitting Target) */}
                    <div className="relative w-64 h-64">
                         <svg viewBox="0 0 200 200" className="w-full h-full drop-shadow-2xl">
                            {/* Target */}
                            <circle cx="100" cy="100" r="90" fill="#1e3a5f" />
                            <circle cx="100" cy="100" r="70" fill="white" />
                            <circle cx="100" cy="100" r="50" fill="#1e3a5f" />
                            <circle cx="100" cy="100" r="30" fill="#f59e0b" />
                            
                            {/* Arrow */}
                            <path d="M160 40 L100 100" stroke="#f59e0b" strokeWidth="12" strokeLinecap="round" />
                            
                            {/* Arrow Feathers */}
                            <path d="M160 40 L140 40" stroke="#f59e0b" strokeWidth="12" strokeLinecap="round" />
                            <path d="M160 40 L160 60" stroke="#f59e0b" strokeWidth="12" strokeLinecap="round" />
                         </svg>
                    </div>
                </div>
                <div>
                    <h2 className="text-3xl font-bold text-navy mb-6">Notre Mission</h2>
                    <p className="text-gray-900 font-medium text-lg leading-relaxed bg-white/60 p-6 rounded-2xl backdrop-blur-sm">
                        Simplifier l'accès au logement et aux opportunités immobilières pour tous, en combinant une expertise locale pointue et l'innovation digitale. Nous connectons les rêves à la réalité.
                    </p>
                </div>
            </div>

            <div className="mb-20">
                <h2 className="text-3xl font-bold text-navy mb-10 text-center bg-white/60 inline-block px-8 py-2 rounded-full backdrop-blur-sm mx-auto block w-fit">Nos Valeurs</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {[
                        { icon: <Heart size={32} />, title: 'Confiance', text: 'La base de toutes nos relations.' },
                        { icon: <Lightbulb size={32} />, title: 'Innovation', text: 'Des solutions modernes pour vos besoins.' },
                        { icon: <Target size={32} />, title: 'Excellence', text: 'Une exigence de qualité constante.' },
                        { icon: <Globe size={32} />, title: 'Proximité', text: 'Toujours à vos côtés, où que vous soyez.' },
                    ].map((val, idx) => (
                        <div key={idx} className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl text-center hover:bg-white hover:shadow-lg transition duration-300 border border-white/50">
                            <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-navy shadow-sm">
                                {val.icon}
                            </div>
                            <h3 className="font-bold text-navy text-xl mb-2">{val.title}</h3>
                            <p className="text-gray-600 text-sm">{val.text}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-navy/95 backdrop-blur-sm rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden shadow-2xl">
                <div className="relative z-10">
                    <h2 className="text-3xl font-bold mb-4">Notre Engagement</h2>
                    <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                      Nous accompagnons chaque client avec la même exigence : transparence, professionnalisme et résultats. Que vous cherchiez à louer, acheter, vendre ou gérer, nous sommes à vos côtés.
                    </p>
                    <a 
                      href="https://wa.me/22997406767" 
                      target="_blank" 
                      rel="noreferrer"
                      className="inline-flex bg-orange hover:bg-orange/90 text-white font-bold py-4 px-8 rounded-xl items-center transition shadow-lg shadow-orange/20"
                    >
                        <MessageCircle size={20} className="mr-2" />
                        Nous contacter sur WhatsApp
                    </a>
                </div>
            </div>

        </div>
      </div>
    </div>
  );
};

export default About;
